import { domQuerySelector } from 'pf-frontend-common/src/helper/dom/query-selector';
import { domQuerySelectorAll } from 'pf-frontend-common/src/helper/dom/query-selector-all';
import { DataKeyValueStringInterface } from 'pf-frontend-common/src/module/data/key-value/string.interface';
import { DomElementEvent } from 'pf-frontend-common/src/module/dom/element.event';
import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';
import { EventEmitterAwareInterface } from 'pf-frontend-common/src/module/event/emitter-aware.interface';
import { ViewBasicInterface } from 'pf-frontend-common/src/module/view/basic.interface';

import { ContentGroupRegisteredInterface } from './registered.interface';
import { ContentGroupStrategyInterface } from './strategy.interface';
import { ContentGroupViewEvent } from './view.event';
import { ContentGroupViewOptionsInterface } from './view-options.interface';

/**
 * Manage generic groups of html buttons and associated content. See Tab and Accordion strategies for individual usage
 */
export class ContentGroupView implements ViewBasicInterface, EventEmitterAwareInterface {
  /**
   * View main HTML element
   */
  protected element: HTMLElement;

  /**
   * List of registered sections
   */
  protected sectionRegisteredList: ContentGroupRegisteredInterface[] = [];

  /**
   * Name of the active section
   */
  protected activeSections: { [key: string]: boolean } = {};

  /**
   * Strategy to apply to content group
   */
  private strategy: ContentGroupStrategyInterface;

  /**
   * Css selectors
   */
  private cssSelector: DataKeyValueStringInterface = {
    activeButton: '.{activeButtonClassName}',
  };

  /**
   * Constructor
   */
  constructor(protected eventEmitter: EventEmitterInterface) {}

  /**
   * @inheritDoc
   */
  public initialize(options: ContentGroupViewOptionsInterface): void {
    this.element = options.element;

    this.setStrategy(options.strategy);
  }

  /**
   * @inheritDoc
   */
  public render(): void {
    // Rendered sections
    const buttons = domQuerySelectorAll(this.element, this.strategy.cssSelector.buttons);
    const activeSections = domQuerySelectorAll(
      this.element,
      this.cssSelector.activeButton.replace('{activeButtonClassName}', this.strategy.cssClass.buttonActive)
    );

    Array.prototype.forEach.call(buttons, (button: HTMLElement) => {
      const name = button.dataset[this.strategy.dataAttr.buttonTargetAccessor];

      this.register(name);
      this.deactivate(name);
    });

    activeSections.forEach((section) => {
      this.activate(section.dataset[this.strategy.dataAttr.buttonTargetAccessor]);
    });
  }

  /**
   * @inheritDoc
   */
  public getEventEmitter(): EventEmitterInterface {
    return this.eventEmitter;
  }

  /**
   * Registers a new section identified by @name
   */
  public register(name: string): void {
    const button = this.getButtonElement(name);
    const content = this.getContentElement(name);

    if (!button || !content) {
      throw Error(`Content group component - No button or content found: ${name}`);
    }

    // Register section
    this.sectionRegisteredList.push({
      name,
      button,
      content,
    });

    // Attach event
    button.addEventListener(DomElementEvent.click, this.onClickContentGroupButton);
  }

  /**
   * Unregisters an existing tab identified by @name
   */
  public unregister(name: string): void {
    const button = this.getButtonElement(name);

    if (!button) {
      throw Error(`Content group component - No button found: ${name}`);
    }

    // Find section pair
    const section = this.sectionRegisteredList.filter((accordionElement: ContentGroupRegisteredInterface) => {
      return accordionElement.name === name;
    })[0];

    if (!section) {
      throw Error(`Content group component - No content found: ${name}`);
    }

    // Remove event
    section.button.removeEventListener(DomElementEvent.click, this.onClickContentGroupButton);
  }

  /**
   * Activate tab identified by @name
   */
  public activate(name: string): void {
    const button = this.getButtonElement(name);
    const content = this.getContentElement(name);

    // Update button and content CSS classes
    button.classList.add(this.strategy.cssClass.buttonActive);
    content.classList.add(this.strategy.cssClass.contentActive);

    // Keep active tab name
    this.activeSections[name] = true;

    this.getEventEmitter().emit(ContentGroupViewEvent.activate, name);

    this.strategy.activate(this, name);
  }

  /**
   * Deactivate group identified by @name
   */
  public deactivate(name: string): void {
    const button = this.getButtonElement(name);
    const content = this.getContentElement(name);

    // Update button and content CSS classes
    button.classList.remove(this.strategy.cssClass.buttonActive);
    content.classList.remove(this.strategy.cssClass.contentActive);

    delete this.activeSections[name];

    this.getEventEmitter().emit(ContentGroupViewEvent.deactivate, name);

    this.strategy.deactivate(this, name);
  }

  /**
   * Returns the button element identified by @name, or null if not found
   */
  public getButtonElement(name: string): HTMLElement | null {
    return domQuerySelector(this.element, this.strategy.cssSelector.button.replace('{name}', name));
  }

  /**
   * Returns the content element identified by @name, or null if not found
   */
  public getContentElement(name: string): HTMLElement | null {
    return domQuerySelector(this.element, this.strategy.cssSelector.content.replace('{name}', name));
  }

  /**
   * Return currently active content group
   */
  public getActiveSections(): { [key: string]: boolean } {
    return this.activeSections;
  }

  /**
   * Return a list of registered content sections
   */
  public getRegistered(): string[] {
    return this.sectionRegisteredList.map((val) => val.name);
  }

  /**
   * Is @param name registered content section ?
   */
  public registered(name: string): boolean {
    return this.getRegistered().indexOf(name) > -1;
  }

  /**
   * Change the active content section
   */
  public change(name: string): void {
    this.strategy.change(this, name);
  }

  /**
   * Clicked on a section header
   */
  protected onClickContentGroupButton = (e: Event): void => {
    const button = <HTMLElement>e.currentTarget;
    const name = button.dataset[this.strategy.dataAttr.buttonTargetAccessor];

    this.change(name);

    this.getEventEmitter().emit(ContentGroupViewEvent.clickContentGroupButton, button);
  };

  /**
   * Set the strategy of the view instance
   */
  protected setStrategy(strategy: ContentGroupStrategyInterface): void {
    this.strategy = strategy;
  }
}
